import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { useParams } from "react-router-dom";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
} from "reactstrap";
import { getSponsorList,getAllSponsorTypes,createSponsor,deleteSponsor,updateSponsor } from "../../../api";
import ImageCropper from "../../../components/common/ImageCropper/ImageCropper";
import ConfirmationDialog from "../../../components/common/ConfirmationDialog/ConfirmationDialog";
import { useNightMode } from "../../../contexts/NightModeContext";
import Background from "../../Login/Background";
import PageHeader from "../../../components/common/PageHeader/PageHeader";
import { Search } from "lucide-react";
import ListCard from "../../../components/common/ListCard/ListCard";

const Sponsor = () => {
  const { isNightMode } = useNightMode();
  const [modal, setModal] = useState(false);
  const toggle = () => setModal(!modal);
  const [sponsors, setSponsors] = useState<any>([]);
  const [originalSponsors, setSponsorsList] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [allSponsorTypes, setAllSponsorTypes] = useState<any[]>([]);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; row: any | null }>({ isOpen: false, row: null });
  const [isLoading, setIsLoading] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [editModal, setEditModal] = useState(false);
  const [editData, setEditData] = useState<any>(null);
  const [editPreviewImage, setEditPreviewImage] = useState(
    editData?.thumb ?? ""
  ); 

  useEffect(() => {
    if (editData) {
      setEditPreviewImage(editData.thumb);
    }
  }, [editData]);

  const { slug } = useParams();      
  useEffect(() => {
    getSponsors();
    getSponsorTypes();
  }, [slug]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      sponsorSelection: (sponsors && sponsors.sponsorSelection) || "",
      sponsorName: (sponsors && sponsors.sponsorName) || "",
      sponsorWebsiteUrl: (sponsors && sponsors.sponsorWebsiteUrl) || "",
      thumb: (sponsors && sponsors.thumb) || "",
    },
    validationSchema: Yup.object({
      sponsorSelection: Yup.string().required("Selection is Required"),
      sponsorName: Yup.string().required("Sponsor Name is Required"),
      thumb: Yup.mixed().required("Sponsor image required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        setIsLoading(true);
        const eventData = localStorage.getItem("eventData");
        if (eventData) {
          const slug = JSON.parse(eventData).slugId;
          const formData = new FormData();
          formData.append("sponsorTypeId", values.sponsorSelection);
          formData.append("name", values.sponsorName || "Eventox");
          formData.append("link", values.sponsorWebsiteUrl || "#");
          formData.append("eventSlug", slug);
          formData.append("thumb", values.thumb);

          await createSponsor(formData);
          toast.success("Sponsor created successfully");
          toggle();
          resetForm();
          getSponsors();
        }
      } catch (error) {
        toast.error("Failed to create sponsor");
      } finally {
        setIsLoading(false);
      }
    },
  });

  const editValidation = useFormik({ 
    enableReinitialize: true,
    initialValues: {
      sponsorId: editData?.sponsorId,
      sponsorSelection: editData?.sponsorTypeId  ?? '',
      sponsorName: editData?.name ?? '',
      sponsorWebsiteUrl: editData?.link ?? '',
      thumb: '' // initially empty
    },
    validationSchema: Yup.object({ 
      sponsorSelection: Yup.string().required("Selection is Required"),
      sponsorName: Yup.string().required("Sponsor Name is Required"),
    }),
    onSubmit: async (values) => {
      try {
        setIsLoading(true);
        const formData = new FormData();
        formData.append("sponsorId", values.sponsorId);
        formData.append("sponsorTypeId", values.sponsorSelection);
        formData.append("name", values.sponsorName);
        formData.append("link", values.sponsorWebsiteUrl);
        if (values.thumb) {
          formData.append("thumb", values.thumb);
        }
        await updateSponsor(values.sponsorId, formData);
        toast.success("Sponsor updated successfully!");
        setEditModal(false);
        getSponsors();
      } catch (error) {
        toast.error("Failed to update sponsor.");
      } finally {
        setIsLoading(false);
      }
    },
  });

  const getSponsors = async () => {
    try {
      const eventData = localStorage.getItem("eventData");
      if (eventData) {
        const eventSlug = JSON.parse(eventData).slugId;
        const response = await getSponsorList();
        if (response && response.data) {
          const filteredAndSortedSponsors: any[] = [];
          response.data.forEach((sponsorItem: any) => {
            if (sponsorItem.eventSlug === eventSlug) {
              filteredAndSortedSponsors.push({
                title: sponsorItem.sponsorType?.title || "N/A", 
                name: sponsorItem.name,
                link: sponsorItem.link,
                thumb: sponsorItem.thumb,
                sponsorId: sponsorItem.sponsorId,
                sponsorTypeId: sponsorItem.sponsorTypeId,
                createdAt: sponsorItem.createdAt || sponsorItem.createdDate || "",
              });
            }
          });
          filteredAndSortedSponsors.sort((a, b) =>
            a.name.localeCompare(b.name)
          );

          setSponsors(filteredAndSortedSponsors);
          setSponsorsList(filteredAndSortedSponsors);
        }
      }
    } catch (error) {
      console.error("Error fetching sponsors:", error);
    } finally {
      setLoading(false);
    }
  };

  const getSponsorTypes = async () => {
    try {
      const response = await getAllSponsorTypes();
      setAllSponsorTypes(response);
    } catch (error) {
      console.error("Error fetching sponsor types:", error);
    }
  };

  const handleDelete = (row: any) => {
    setDeleteConfirm({ isOpen: true, row });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.row) return;
    try {
      await deleteSponsor(deleteConfirm.row.sponsorId);
      toast.success("Sponsor deleted successfully!");
      getSponsors();
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || "Failed to delete sponsor";
      toast.error(errorMsg);
    } finally {
      setDeleteConfirm({ isOpen: false, row: null });
    }
  };

  const handleSearch = (e: any) => {
    const searchTerm = e.target.value;
    setSearch(searchTerm);
    if (searchTerm.length > 0) {
      const tempSponsors = originalSponsors.filter((sponsor: any) =>
        sponsor.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setSponsors(tempSponsors);
    } else {
      setSponsors(originalSponsors); 
    }
  };    

  const handleEditClick = (item: any) => {
    setEditData(item);
    setEditPreviewImage(item.thumb);
    setEditModal(true);
  };

  return (
    <div className={`contact-list-container ${isNightMode ? 'text-white' : 'text-dark'}`}>
      <Background />
      <ToastContainer autoClose={2000} />
      <PageHeader
        title="Sponsors"
        subtitle="Manage event sponsors, sponsor tiers, website links, branding assets, and display orders."
        isLoading={loading}
        actions={
          <Button color="danger" onClick={toggle} className="v3-btn-primary h_40 d-flex align-items-center">
            <i className="fa-solid fa-plus me-2"></i> New Sponsor
          </Button>
        }
      />

      <div className="contact-filter-card mb-4 mt-4">
        <div className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-3">
          <div className="position-relative flex-grow-1 w-100">
            <Search className="position-absolute start-0 top-50 translate-middle-y ms-3 contact-sub-text" size={18} />
            <input
              type="text"
              className="contact-search-input"
              placeholder="Search sponsors by name..."
              value={search}
              onChange={handleSearch}
            />
          </div>
        </div>
      </div>

      <div>
        <Modal modalClassName="quick-v2-glass-modal" isOpen={modal} toggle={toggle} size="lg" centered>
          <ModalHeader toggle={toggle}>
            ADD NEW SPONSOR
          </ModalHeader>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
            }}
          >
            <ModalBody>
              <FormGroup className="mb-3">
                <Label for="sponsorName" className="form-label">
                  SPONSOR NAME <span className="text-danger">*</span>
                </Label>
                <Input
                  id="sponsorName"
                  name="sponsorName"
                  type="text"
                  placeholder="Enter Sponsor Name"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.sponsorName || ""}
                  invalid={validation.touched.sponsorName && !!validation.errors.sponsorName}
                />
                {validation.touched.sponsorName && validation.errors.sponsorName && (
                  <FormFeedback type="invalid" className="d-block text-danger mt-1">
                    {validation.errors.sponsorName as string}
                  </FormFeedback>
                )}
              </FormGroup>

              <FormGroup className="mb-3">
                <Label for="selectedEvent" className="form-label">
                  SELECT SPONSOR TYPE <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  id="selectedEvent"
                  name="sponsorSelection"
                  value={validation.values.sponsorSelection}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  invalid={validation.touched.sponsorSelection && !!validation.errors.sponsorSelection}
                >
                  <option value="">Select a sponsor type</option>
                  {allSponsorTypes.map((type: any) => (
                    <option key={type.sponsorTypeId} value={type.sponsorTypeId}>
                      {type.title}
                    </option>
                  ))}
                </Input>
                {validation.touched.sponsorSelection && validation.errors.sponsorSelection && (
                  <FormFeedback type="invalid" className="d-block text-danger mt-1">
                    {String(validation.errors.sponsorSelection)}
                  </FormFeedback>
                )}
              </FormGroup>

              <FormGroup className="mb-3">
                <Label for="sponsorWebsiteUrl" className="form-label">
                  WEBSITE URL
                </Label>
                <Input
                  id="sponsorWebsiteUrl"
                  name="sponsorWebsiteUrl"
                  type="text"
                  placeholder="https://example.com"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.sponsorWebsiteUrl || ""}
                  invalid={validation.touched.sponsorWebsiteUrl && !!validation.errors.sponsorWebsiteUrl}
                />
                {validation.touched.sponsorWebsiteUrl && validation.errors.sponsorWebsiteUrl && (
                  <FormFeedback type="invalid" className="d-block text-danger mt-1">
                    {validation.errors.sponsorWebsiteUrl as string}
                  </FormFeedback>
                )}
              </FormGroup>

              <FormGroup className="mb-3">
                <Label for="image" className="form-label">
                  SPONSOR IMAGE <span className="text-danger">*</span>
                </Label>
                <style>{`
                  .sponsor-file-input {
                    display: flex !important;
                    align-items: center !important;
                    height: 46px !important;
                    padding: 4px 8px !important;
                  }
                  .sponsor-file-input::-webkit-file-upload-button,
                  .sponsor-file-input::file-selector-button {
                    margin: 0 12px 0 0 !important;
                    border-radius: 6px !important;
                    height: 34px !important;
                    padding: 0 14px !important;
                    display: inline-flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    border: none !important;
                    background: #ffffff !important;
                    color: #222222 !important;
                    font-weight: 500 !important;
                    cursor: pointer !important;
                  }
                `}</style>
                <Input
                  id="image"
                  name="thumb"
                  type="file"
                  accept="image/*"
                  className="sponsor-file-input"
                  invalid={validation.touched.thumb && !!validation.errors.thumb}
                  onChange={(event) => {
                    const file = event.currentTarget.files?.[0];
                    if (file) {
                      if (!file.type.startsWith('image/')) {
                        toast.error("Only image files are allowed");
                        return;
                      }
                      if (file.size > 5 * 1024 * 1024) {
                        toast.error("Max file size is 5MB");
                        return;
                      }
                      setSelectedImageFile(file);  
                      validation.setFieldValue("thumb", file);
                      setShowCropper(true);        
                    }
                  }}
                  onBlur={validation.handleBlur}
                />
                <small className="text-muted d-block mt-1">Recommended size: 200×200. Max 5MB.</small>
                {validation.touched.thumb && validation.errors.thumb && (
                  <FormFeedback type="invalid" className="d-block text-danger mt-1">
                    {validation.errors.thumb as string}
                  </FormFeedback>
                )}
              </FormGroup>
            </ModalBody>
            <ModalFooter className="d-flex justify-content-end gap-2 border-top-0 pt-0">
              <Button color="secondary" type="button" outline onClick={toggle} disabled={isLoading}>
                Cancel
              </Button>
              <Button color="primary" type="submit" disabled={isLoading} className="btn-primary rounded-pill px-4">
                <i className="fa-solid fa-floppy-disk me-2"></i>
                {isLoading ? "Saving..." : "Save Sponsor"}
              </Button>
            </ModalFooter>
          </Form>
        </Modal> 

        <Modal modalClassName="quick-v2-glass-modal" isOpen={showCropper} toggle={() => setShowCropper(false)} centered>
          <ModalHeader toggle={() => setShowCropper(false)}>CROP IMAGE</ModalHeader>
          <ModalBody className="p-3">
            {selectedImageFile && (
              <ImageCropper
                imageFile={selectedImageFile}
                onCropComplete={(croppedFile) => {
                  if (editModal) {
                    editValidation.setFieldValue("thumb", croppedFile);
                  } else {
                    validation.setFieldValue("thumb", croppedFile);
                  }
                  setEditPreviewImage(URL.createObjectURL(croppedFile)); 
                  setShowCropper(false);
                }}
              />
            )}
          </ModalBody>
        </Modal>

        <Modal modalClassName="quick-v2-glass-modal" isOpen={editModal} toggle={() => setEditModal(false)} size="lg" centered>
          <ModalHeader toggle={() => setEditModal(false)}>
            EDIT SPONSOR
          </ModalHeader>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              editValidation.handleSubmit();
            }}
          >
            <ModalBody>
              <FormGroup className="mb-3">
                <Label for="editSponsorName" className="form-label">
                  SPONSOR NAME <span className="text-danger">*</span>
                </Label>
                <Input
                  id="editSponsorName"
                  name="sponsorName"
                  type="text"
                  placeholder="Enter Sponsor Name"
                  onChange={editValidation.handleChange}
                  onBlur={editValidation.handleBlur}
                  value={editValidation.values.sponsorName}
                  invalid={editValidation.touched.sponsorName && !!editValidation.errors.sponsorName}
                />
                {editValidation.touched.sponsorName && editValidation.errors.sponsorName && (
                  <FormFeedback type="invalid" className="d-block text-danger mt-1">
                    {editValidation.errors.sponsorName as string}
                  </FormFeedback>
                )}
              </FormGroup>

              <FormGroup className="mb-3">
                <Label for="editSponsorSelection" className="form-label">
                  SELECT SPONSOR TYPE <span className="text-danger">*</span>
                </Label>
                <Input
                  id="editSponsorSelection"
                  name="sponsorSelection"
                  type="select"
                  value={editValidation.values.sponsorSelection}
                  onChange={editValidation.handleChange}
                  onBlur={editValidation.handleBlur}
                  invalid={editValidation.touched.sponsorSelection && !!editValidation.errors.sponsorSelection}
                >
                  <option value="">Select a sponsor type</option>
                  {allSponsorTypes.map((type: any) => (
                    <option key={type.sponsorTypeId} value={type.sponsorTypeId}>
                      {type.title}
                    </option>
                  ))}
                </Input>
                {editValidation.touched.sponsorSelection && editValidation.errors.sponsorSelection && (
                  <FormFeedback type="invalid" className="d-block text-danger mt-1">
                    {editValidation.errors.sponsorSelection as string}
                  </FormFeedback>
                )}
              </FormGroup>

              <FormGroup className="mb-3">
                <Label for="editSponsorWebsiteUrl" className="form-label">
                  WEBSITE URL
                </Label>
                <Input
                  id="editSponsorWebsiteUrl"
                  name="sponsorWebsiteUrl"
                  type="text"
                  placeholder="https://example.com"
                  onChange={editValidation.handleChange}
                  onBlur={editValidation.handleBlur}
                  value={editValidation.values.sponsorWebsiteUrl}
                  invalid={editValidation.touched.sponsorWebsiteUrl && !!editValidation.errors.sponsorWebsiteUrl}
                />
                {editValidation.touched.sponsorWebsiteUrl && editValidation.errors.sponsorWebsiteUrl && (
                  <FormFeedback type="invalid" className="d-block text-danger mt-1">
                    {editValidation.errors.sponsorWebsiteUrl as string}
                  </FormFeedback>
                )}
              </FormGroup>

              <FormGroup className="mb-3">
                <Label for="editSponsorImage" className="form-label">
                  SPONSOR IMAGE
                </Label>
                {editPreviewImage && (
                  <div className="mb-2">
                    <img
                      src={editPreviewImage}
                      alt="Sponsor"
                      style={{ width: "120px", height: "120px", objectFit: "cover", borderRadius: "8px", display: "block" }}
                    />
                  </div>
                )}
                <Input
                  id="editSponsorImage"
                  name="thumb"
                  type="file"
                  accept="image/*"
                  className="sponsor-file-input"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) {
                      if (!file.type.startsWith('image/')) {
                        toast.error("Only image files are allowed");
                        return;
                      }
                      if (file.size > 5 * 1024 * 1024) {
                        toast.error("Max file size is 5MB");
                        return;
                      }
                      setSelectedImageFile(file); 
                      editValidation.setFieldValue("thumb", file);
                      setShowCropper(true);        
                    }
                  }}
                />
                <small className="text-muted d-block mt-1">Recommended size: 200×200. Max 5MB.</small>
              </FormGroup>
            </ModalBody>
            <ModalFooter className="d-flex justify-content-end gap-2 border-top-0 pt-0">
              <Button color="secondary" type="button" outline onClick={() => setEditModal(false)} disabled={isLoading}>
                Cancel
              </Button>
              <Button color="primary" type="submit" disabled={isLoading} className="btn-primary rounded-pill px-4">
                <i className="fa-solid fa-floppy-disk me-2"></i>
                {isLoading ? "Saving..." : "Save Sponsor"}
              </Button>
            </ModalFooter>
          </Form>
        </Modal>
      </div>

      <div className="mt-4">
        {sponsors && sponsors.length > 0 ? (
          sponsors.map((sponsor: any) => (
            <ListCard
              key={sponsor.sponsorId}
              cardData={{
                iconImage: sponsor.thumb,
                name: sponsor.name,
                ticket: sponsor.title,
                link: sponsor.link,
                startDate: sponsor.createdAt,
              }}
              onEdit={() => handleEditClick(sponsor)}
              onDelete={() => handleDelete(sponsor)}
            />
          ))
        ) : (
          <p className="text-muted">No sponsors available</p>
        )}
      </div>

      <ConfirmationDialog
        isOpen={deleteConfirm.isOpen}
        toggle={() => setDeleteConfirm({ isOpen: false, row: null })}
        onConfirm={confirmDelete}
        title="Delete Sponsor"
        message={`Are you sure you want to permanently delete <strong>${deleteConfirm.row?.name || 'this sponsor'}</strong>? This action cannot be undone.`}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        type="danger"
        icon="fa-solid fa-trash"
      />
    </div>
  );
};

export default Sponsor;